import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import authService from '../../../services/authService';
import candidateService from '../../../services/candidateService';
import dashboardService from '../../../services/dashboardService';
import Header from '../../../components/ui/Header';
import FixedSidebar from '../../../components/ui/FixedSidebar';
import Breadcrumb from '../../../components/ui/Breadcrumb';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { hasFeatureAccess, hasActionAccess } from '../../../utils/roleBasedAccess';

/**
 * HR Coordinator Dashboard
 * Focused on task coordination, workflow management, and operational support
 */
const HRCoordinatorDashboard = () => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [taskStats, setTaskStats] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const loadCoordinatorData = async () => {
      console.log('[HRCoordinatorDashboard] Loading coordinator data');
      setLoading(true);
      setError(null);
      
      try {
        // Get current user
        const user = authService.getStoredUser() || await authService.getCurrentUser();
        setCurrentUser(user);
        
        // Load dashboard overview
        const overview = await dashboardService.getDashboardOverview();
        setDashboardData(overview);
        
        // Mock task statistics for now - in production, this would come from a task service
        setTaskStats({
          myTasks: 8,
          pendingTasks: 3,
          completedToday: 5,
          overdueTasks: 1
        });
        
      } catch (error) {
        console.error('[HRCoordinatorDashboard] Error loading data:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    loadCoordinatorData();
  }, []);

  const notificationCounts = {
    tasks: taskStats?.pendingTasks || 0,
    candidates: dashboardData?.pendingCandidates || 0,
    interviews: dashboardData?.todaysInterviews || 0,
    total: (taskStats?.pendingTasks || 0) + (dashboardData?.pendingCandidates || 0) + (dashboardData?.todaysInterviews || 0)
  };

  const coordinatorMetrics = [
    {
      title: 'My Tasks',
      value: taskStats?.myTasks?.toString() || '0',
      change: '+2',
      changeType: 'increase',
      icon: 'CheckSquare',
      color: 'primary',
      description: 'Active assignments'
    },
    {
      title: 'Pending Reviews',
      value: taskStats?.pendingTasks?.toString() || '0',
      change: '-1',
      changeType: 'decrease',
      icon: 'Clock',
      color: 'warning',
      description: 'Awaiting action'
    },
    {
      title: 'Completed Today',
      value: taskStats?.completedToday?.toString() || '0',
      change: '+3',
      changeType: 'increase',
      icon: 'CheckCircle',
      color: 'success',
      description: 'Tasks finished today'
    },
    {
      title: 'Overdue Tasks',
      value: taskStats?.overdueTasks?.toString() || '0',
      change: '0',
      changeType: 'neutral',
      icon: 'AlertTriangle',
      color: 'danger',
      description: 'Require immediate attention'
    }
  ];

  const quickActions = [
    {
      title: 'Manage Tasks',
      description: 'View and manage your task assignments',
      icon: 'List',
      color: 'primary',
      action: () => navigate('/hr-roles/coordinator/tasks')
    },
    {
      title: 'Schedule Interview',
      description: 'Coordinate interview scheduling',
      icon: 'Calendar',
      color: 'success',
      action: () => navigate('/interview-scheduling')
    },
    {
      title: 'Candidate Database',
      description: 'Search and review candidates',
      icon: 'Users',
      color: 'info',
      action: () => navigate('/candidate-database')
    },
    {
      title: 'Upload CVs',
      description: 'Process new candidate resumes',
      icon: 'Upload',
      color: 'warning',
      action: () => navigate('/cv-upload-processing')
    }
  ];

  const todayTasks = [
    {
      id: 1,
      title: 'Review candidate screening results',
      priority: 'high',
      dueTime: '10:00 AM',
      status: 'pending'
    },
    {
      id: 2,
      title: 'Coordinate interview with John Smith',
      priority: 'high',
      dueTime: '2:00 PM',
      status: 'in-progress'
    },
    {
      id: 3,
      title: 'Update candidate status reports',
      priority: 'medium',
      dueTime: '4:00 PM',
      status: 'pending'
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header currentUser={currentUser} notificationCounts={notificationCounts} />
        <FixedSidebar currentPath="/hr-roles/coordinator" />
        <main className="ml-72 pt-16">
          <div className="flex items-center justify-center h-96">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading coordinator dashboard...</p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <Header currentUser={currentUser} notificationCounts={notificationCounts} />
        <FixedSidebar currentPath="/hr-roles/coordinator" />
        <main className="ml-72 pt-16">
          <div className="flex items-center justify-center h-96">
            <div className="text-center">
              <Icon name="AlertCircle" size={48} className="text-red-500 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-gray-800 mb-2">Failed to Load Dashboard</h2>
              <p className="text-gray-600 mb-4">{error}</p>
              <Button onClick={() => window.location.reload()}>Retry</Button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header currentUser={currentUser} notificationCounts={notificationCounts} />
      <FixedSidebar currentPath="/hr-roles/coordinator" />
      
      <main className="ml-72 pt-16">
        <div className="p-6">
          <Breadcrumb customBreadcrumbs={[
            { label: 'HR Roles', href: '/hr-roles' },
            { label: 'Coordinator Dashboard', href: '/hr-roles/coordinator', isActive: true }
          ]} />

          {/* Page Header */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">Coordinator Dashboard</h1>
              <p className="text-muted-foreground">
                Manage tasks, coordinate workflows, and support HR operations
              </p>
            </div>
            <div className="flex items-center space-x-3 mt-4 lg:mt-0">
              <Button variant="outline" onClick={() => navigate('/hr-roles/coordinator/tasks')}>
                <Icon name="List" size={16} className="mr-2" />
                Manage Tasks
              </Button>
              <Button onClick={() => navigate('/interview-scheduling')}>
                <Icon name="Calendar" size={16} className="mr-2" />
                Schedule Interview
              </Button>
            </div>
          </div>

          {/* Metrics Overview */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {coordinatorMetrics.map((metric, index) => (
              <div key={index} className="bg-card rounded-lg p-6 border border-border">
                <div className="flex items-center justify-between mb-4">
                  <div className={`p-2 rounded-lg bg-${metric.color}/10`}>
                    <Icon 
                      name={metric.icon} 
                      size={24} 
                      className={`text-${metric.color}`} 
                    />
                  </div>
                  <div className={`text-sm font-medium ${
                    metric.changeType === 'increase' ? 'text-green-600' :
                    metric.changeType === 'decrease' ? 'text-red-600' :
                    'text-gray-500'
                  }`}>
                    {metric.change}
                  </div>
                </div>
                <div className="space-y-1">
                  <p className="text-2xl font-bold text-foreground">{metric.value}</p>
                  <p className="text-sm font-medium text-foreground">{metric.title}</p>
                  <p className="text-xs text-muted-foreground">{metric.description}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Quick Actions */}
            <div className="lg:col-span-2">
              <div className="bg-card rounded-lg border border-border p-6">
                <h2 className="text-xl font-semibold text-foreground mb-4">Quick Actions</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {quickActions.map((action, index) => (
                    <button
                      key={index}
                      onClick={action.action}
                      className="p-4 border border-border rounded-lg hover:bg-accent hover:border-accent-foreground transition-colors text-left group"
                    >
                      <div className="flex items-start space-x-3">
                        <div className={`p-2 rounded-lg bg-${action.color}/10 group-hover:bg-${action.color}/20 transition-colors`}>
                          <Icon 
                            name={action.icon} 
                            size={20} 
                            className={`text-${action.color}`} 
                          />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-medium text-foreground mb-1">{action.title}</h3>
                          <p className="text-sm text-muted-foreground">{action.description}</p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Today's Tasks */}
            <div>
              <div className="bg-card rounded-lg border border-border p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-semibold text-foreground">Today's Tasks</h2>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => navigate('/hr-roles/coordinator/tasks')}
                  >
                    View All
                  </Button>
                </div>
                <div className="space-y-3">
                  {todayTasks.map((task) => (
                    <div key={task.id} className="p-3 border border-border rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          task.priority === 'high' ? 'bg-red-100 text-red-800' :
                          task.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-green-100 text-green-800'
                        }`}>
                          {task.priority}
                        </span>
                        <span className="text-xs text-muted-foreground">{task.dueTime}</span>
                      </div>
                      <p className="text-sm font-medium text-foreground mb-1">{task.title}</p>
                      <div className="flex items-center space-x-1">
                        <div className={`w-2 h-2 rounded-full ${
                          task.status === 'completed' ? 'bg-green-500' :
                          task.status === 'in-progress' ? 'bg-blue-500' :
                          'bg-gray-300'
                        }`}></div>
                        <span className="text-xs text-muted-foreground capitalize">
                          {task.status.replace('-', ' ')}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default HRCoordinatorDashboard;